import axios from 'axios';

import { FlowType } from '@ply-io/ply-front-shared-utilities';

import { useAccountEmailDomainCrud } from './account_email_domains';
import { useAiAssistantApiDocsCrud } from './api_docs';
import {
	api,
	fullDataResponseApi,
	fullResponseApi,
	autoPaginatedApi,
	apigw,
	setToken,
	removeToken,
	runnerApi,
	flowRunApi,
	nonAuthedApi,
} from './clients';
import { SERVICE_ACTION_COLLECTION_API_RESOURCE_NAME } from './const';
import buildInboxesApi from './inboxes';
import buildNotificationsApi from './notifications';
import buildProcessesApi from './processes';
import { Tasks, useTaskResolver } from './tasks';
import { loginWithToken } from './verify_api';
import { getNodesByIds } from './workflow-nodes';
import { getWorkflow } from './workflows';
import { STATIC_INBOXES_IDS } from '@/components/Inbox/inbox.const.js';
import ErrorHandlingService, { NodeCreationError, NodeUpdateError } from '@/utils/error-handling-service';
import { cleanObjectFromNullishValues } from '@/utils/helpers';

const loginWithUserPass = (username, password) => api.post('api-token-auth/', { username, password });

const loginWithGoogleToken = (token, isInstallable = false) =>
	api.post('users/login/google/', { id_token: token, installable: isInstallable });

const loginWithGoogleCode = (code) => api.post('users/login/google/code/', { code });

const loginWithSSO = (email) => nonAuthedApi.post(`sso/login/`, { email });
const loginWithSSOCode = (code) => nonAuthedApi.post('sso/callback/', { code });

const logout = () => api.post('logout/');

const changePassword = (userId, currentPassword, newPassword, newPasswordVerify) =>
	api.put(`users/${userId}/password/`, {
		current_password: currentPassword,
		new_password: newPassword,
		new_password_verify: newPasswordVerify,
	});

const forgotPassword = (email) => api.post('forgot-password/', { email });

const resetPassword = (userID, token, newPassword, newPasswordConfirmation) =>
	api.post('reset-password/', { id: userID, token, new_password: newPassword, new_password2: newPasswordConfirmation });

const validateReset = (userID, token) => api.post('validate-reset/', { id: userID, token });

const getOneTimeToken = () => api.post('one-time-token/');

const verifyOneTimeToken = ({ token, email }) => api.post('one-time-token/verification/', { token, email });

const getMarketplace = (marketplaceId) => api.get(`marketplaces/${marketplaceId}/`);

const getUser = (userId) => api.get(`users/${userId}/`);

const sendReminderToUseDesktop = () => api.post(`users/reminders/`, { type: 'mobile_signup' });
const sendContinueInstallationSignupMail = (payload) => api.post(`users/reminders/`, { type: 'installation_signup', ...payload });

const makeSyncedGroupManual = (groupId) => api.post(`groups/${groupId}/sync/migration/`);

const getGroups = () => autoPaginatedApi.get(`groups/`);

const getGroup = (groupId) => api.get(`groups/${groupId}/`);
const setGroupUsers = (groupId, users) => api.put(`groups/${groupId}/users/`, { users });
const removeGroupUser = (groupId, userId) => api.delete(`groups/${groupId}/users/${userId}/`);
const addGroupUser = (groupId, userId) => api.put(`groups/${groupId}/users/${userId}/`);
const createGroup = (payload) => api.post('groups/', payload);
const updateGroup = (group) => api.put(`groups/${group.id}/`, group);
const deleteGroup = (groupId) => api.delete(`groups/${groupId}/`);
const syncGroup = (groupId) => api.post(`groups/${groupId}/sync/`);

const registerUser = (email, firstName, lastName, password, signupCode, signupReferrer, uid, profile, gtoken, isInstallable = false) => {
	const data = {
		email,
		first_name: firstName,
		last_name: lastName,
		password,
		signup_code: signupCode,
		signup_referrer: signupReferrer,
		uid,
		profile,
		gtoken: gtoken || null,
		installable: isInstallable,
	};
	return api.post('users/', data);
};

const createFlow = (workflow) => api.post('workflows/', workflow);

const createFlowFromExistingNodes = (payload) => dispatchAndWaitTask(Tasks.CreateSubflowFromNodes, payload);

const getUserCredentials = (params = {}) => autoPaginatedApi.get(`credentials/`, { params: { ...params } });

const getCredential = (id) => api.get(`credentials/${id}/`);

const getProjectCredentials = (projectId) => api.get(`projects/${projectId}/credentials/`);

const skipTestStep = (nodeId) => api.post(`workflow-nodes/${nodeId}/test/skip/`);

const testStep = (nodeId, payload = {}) => api.post(`workflow-nodes/${nodeId}/test/step/`, payload);

const triggerSetup = (nodeId) => api.post(`workflow-nodes/${nodeId}/setup/trigger/`);

/* BUTTON DROPDOWNS */
const BUTTON_DROPDOWNS_PATH = 'button-dropdowns';
const getButtonDropdowns = (page, filterBy = {}, autoPaginate = false) => {
	const params = Object.entries({ ...filterBy, page }).reduce((params, [key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((v) => params.append(key, v));
		} else {
			params.append(key, value);
		}
		return params;
	}, new URLSearchParams());
	const data = (autoPaginate ? autoPaginatedApi : fullDataResponseApi).get(`${BUTTON_DROPDOWNS_PATH}/`, { params });
	return data;
};

const deleteButtonDropdown = (id) => api.delete(`${BUTTON_DROPDOWNS_PATH}/${id}/`);

const getButtonDropdown = (id) => api.get(`${BUTTON_DROPDOWNS_PATH}/${id}/`);

const updateButtonDropdown = (id, partialObjectToUpdate) => api.patch(`${BUTTON_DROPDOWNS_PATH}/${id}/`, partialObjectToUpdate);

const createButtonDropdown = (payload) => api.post(`${BUTTON_DROPDOWNS_PATH}/`, payload);
/* BUTTON DROPDOWNS */

const getFlows = (page, filterBy = {}, autoPaginate = false) => {
	const params = Object.entries(filterBy).reduce((params, [key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((v) => params.append(key, v));
		} else {
			params.append(key, value);
		}
		return params;
	}, new URLSearchParams());
	if (page) {
		params.append('page', page);
	}
	const data = (autoPaginate ? autoPaginatedApi : fullDataResponseApi).get('workflows/', { params });
	return data;
};

const getFlowsByCredentials = (cid) => {
	// page
	const data = fullDataResponseApi.get(`workflows/credentials/${cid}/`);
	return data;
};

const getProjects = () => autoPaginatedApi.get('projects/');
const getAllProjects = () => autoPaginatedApi.get(`projects/all/`);

const getProject = (id) => api.get(`projects/${id}/`);

const updateProject = (project) => {
	const { id } = project;
	return api.patch(`projects/${id}/`, project);
};

const getProjectSharedData = (projectId) => api.get(`projects/${projectId}/roles/data/`);
const getAllProjectShareData = (projectsIds = []) => {
	const projectsSearchParamKey = 'projects';
	const searchParams = new URLSearchParams();
	projectsIds.forEach((id) => {
		searchParams.append(projectsSearchParamKey, id);
	});
	return api.get(`projects/roles/data/?${searchParams.toString()}`);
};

const getProjectPermissions = (projectId) => api.get(`projects/${projectId}/permissions/`);

const getAllProjectPermissions = () => api.get(`projects/permissions/`);

const updateProjectRoles = (projectId, role, groups = [], emails = []) => api.put(`projects/${projectId}/roles/`, { role, groups, emails });

const getNode = (nodeID) => api.get(`workflow-nodes/${nodeID}/`);

const updateNode = async (node, partial) => {
	try {
		const req = partial ? api.patch : api.put;
		return await req(`workflow-nodes/${node.id}/`, node);
	} catch (err) {
		throw new NodeUpdateError(ErrorHandlingService.extractErrorMessage(err) || 'Unable to perform action');
	}
};

const getWidget = (widgetId, workflowId, workflowVersionId, published) => {
	const params = { workflow_id: workflowId, workflow_version_id: workflowVersionId, published };
	return api.get(`widgets/${widgetId}/`, { params: cleanObjectFromNullishValues(params) });
};

const getWidgets = (widgetIds, workflowVersionId) => {
	const params = Object.entries(widgetIds).reduce((params, [, value]) => {
		params.append('uuid', value);
		return params;
	}, new URLSearchParams());
	params.append('workflow_version_id', workflowVersionId);
	return api.get(`widgets/`, { params });
};

const getOptionsForAction = (nodeID, source, search, inputFieldKey, pageNum, retrievalId, widgetFieldId = null) =>
	fullDataResponseApi.post(`workflow-nodes/${nodeID}/services/options/`, {
		source,
		term: search,
		param_key: inputFieldKey,
		page: pageNum,
		retrievalId,
		widgetFieldId,
	});

const getOptionsForWidget = (
	widgetFieldUuid,
	source,
	search,
	inputFieldKey,
	pageNum,
	retrievalId,
	workflowVersionId,
	credentials = null,
	paramsMap = null
) =>
	fullDataResponseApi.post(`widgetsfields/${widgetFieldUuid}/options/`, {
		term: search,
		service_action: source,
		param_key: inputFieldKey,
		page: pageNum,
		retrievalId,
		widgetFieldId: widgetFieldUuid,
		workflow_version_id: workflowVersionId,
		credentials, // Can be null.
		params_map: paramsMap, // Can be null.
	});

const getExtraFields = (nodeID) => api.get(`workflow-nodes/${nodeID}/params/`);

const toggleNodeType = (nodeID) => api.post(`workflow-nodes/${nodeID}/type/toggle/`);

const saveWidget = (widgetId, fields, widgetTitle, widgetMetadata, workflowVersionId) =>
	api.post(
		`widgets/${widgetId}/save/`,
		{ fields, widgetTitle, metadata: widgetMetadata },
		{ params: { /* workflow_id: workflowId, */ workflow_version_id: workflowVersionId } }
	);

const assignWidgetFieldCredentialsSourceField = (widgetFieldId, workflowVersionId) =>
	api.post(`widgetsfields/${widgetFieldId}/credentials/source/`, { workflow_version_id: workflowVersionId });

const setWidgetFieldCredentialsSourceField = (widgetFieldId, workflowVersionId, credentialsSourceFieldUuid) =>
	api.put(`widgetsfields/${widgetFieldId}/credentials/source/`, {
		workflow_version_id: workflowVersionId,
		credentials_source_field_uuid: credentialsSourceFieldUuid,
	});

const createProject = (projectObj) => api.post(`projects/`, projectObj);

const collaborateNewProject = (projectId, emails, role, emailMessage, isSendMail, invite_user_role) =>
	api.put(`projects/${projectId}/collaborators/`, {
		emails,
		role,
		invite_user_role,
		email_message: emailMessage,
		is_send_mail: isSendMail,
	});

const collaborateGroupsNewProject = (projectId, groups, role, emailMessage, isSendMail) =>
	api.put(`projects/${projectId}/collaborators/groups/`, {
		groups,
		role,
		email_message: emailMessage,
		is_send_mail: isSendMail,
	});

const updateFlow = (flowID, flow) => api.patch(`workflows/${flowID}/`, flow);
const updatePublicFlowIcon = (flowID, image) => {
	const data = new FormData();
	data.append('icon', image);
	return api.patch(`workflows/${flowID}/`, data, {
		'Content-Type': 'multipart/form-data',
	});
};

const { dispatchAndWaitTask } = useTaskResolver();

const publishFlow = async (flowID, configurationsInstallableFlags) =>
	dispatchAndWaitTask(Tasks.PublishWorkflow, { workflow_id: flowID, ...configurationsInstallableFlags });

const testFlow = (flowId) => api.post(`workflows/${flowId}/test/`);

const updateFlowIsActive = (flowId, isActive) => api.put(`workflows/${flowId}/active/`, { is_active: isActive });
const duplicateWorkflow = (flowId, workflowVersionId, title) =>
	dispatchAndWaitTask(Tasks.DuplicateWorkflow, { workflow_id: flowId, title, workflow_version_id: workflowVersionId });

const signForExtensionUpdate = (email, browser) =>
	axios.post(`https://app-server.ply.io/api/incoming/webhooks/O3mgAnP5/?email=${encodeURI(email)}&browser=${encodeURI(browser)}`);

const setFlowWaitingForFlag = (flowID, is_on, waiting_type) => api.put(`workflows/${flowID}/waiting_flag/`, { is_on, waiting_type });
const getFlowWaitingForFlag = (flowID, waiting_type) => api.get(`workflows/${flowID}/waiting_flag/`, { params: { waiting_type } });

// toggle workflow visibility
const toggleWorkflowIsOnSettings = (flowId, payload) => api.put(`workflows/${flowId}/settings/is_on/`, payload /*buddy*/);
const changeGroupFeatureVisibility = (workflowId, groupId, isOn, applyForCurrent) =>
	api.put(`workflows/${workflowId}/settings/is_on/groups/${groupId}/`, { isOn, applyForCurrent });

const applyExistingWorkflowVisibilityToAllGroupMembers = (flowId, groupId) =>
	api.put(`workflows/${flowId}/settings/is_on/groups/${groupId}/`);

const getWorkflowSharedData = (flowId) => api.get(`workflows/${flowId}/share/data/`);

const getWorkflowLockedCredentials = (workflowId, credentialsId) => api.get(`workflows/${workflowId}/assets/credentials/${credentialsId}/`);

const saveUserProfile = (userID, user) => api.patch(`users/${userID}/`, user);

/** put request to save user's avatar image */
const saveUserProfileAvatar = (userId, file) => {
	const fd = new FormData();
	if (file) {
		fd.set('avatar', file, file.name);
	}
	return api.put(`users/${userId}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
const saveWorkspaceAvatar = (accountId, file) => {
	const fd = new FormData();
	if (file) {
		fd.set('avatar', file, file.name);
	}
	return api.patch(`accounts/${accountId}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

const joinWorkspace = (userId, account_link_id, name, company_size, timezone) => {
	const params = { account_link_id, timezone };
	if (name) {
		params.name = name;
	}
	if (company_size) {
		params.company_size = company_size;
	}
	return api.put(`/users/${userId}/accounts/`, params);
};

const updateAccount = (accountId, account) => api.patch(`accounts/${accountId}/`, account);

const saveUserMetadata = (userID, metadata) => api.patch(`users/${userID}/metadata/`, { metadata });

const getAccountMembers = (accountID) => api.get(`accounts/${accountID}/members/`);
const getAccount = (accountID) => api.get(`accounts/${accountID}/`);

const removeUserFromAccount = (userId, accountID) => api.delete(`accounts/${accountID}/members/${userId}`);

const inviteToAccount = (accountID, emails, invite_user_role) =>
	api.post(`accounts/${accountID}/invitation/`, { emails, invite_user_role });

const revokeInvitation = (accountID, uID) => fullResponseApi.delete(`accounts/${accountID}/invitation/${uID}/`);

const resendInvitation = (accountID, uID) => api.put(`accounts/${accountID}/invitation/${uID}/`);

const updateRoles = (accountID, users, roleCode) => api.put(`accounts/${accountID}/roles/`, { users, role: roleCode });

const updateUsersState = (accountID, users, activate) => api.post(`accounts/${accountID}/activation/toggle/`, { users, activate });

const resendConfirmationEmail = (email) => api.post(`users/activate/`, { email });

const updateCredentialsName = (credsID, connectionName) => api.patch(`credentials/${credsID}/rename/`, { name: connectionName });

const getCredentialsWorkflows = (credsID) => api.get(`credentials/${credsID}/workflows/`);

const disconnectCredentials = (credsID) => api.delete(`credentials/${credsID}/`);

const updateCredentialAccess = (credsID, access) => api.patch(`credentials/${credsID}/access/`, { access });

const testConnection = (credsID) => api.get(`credentials/${credsID}/test-connection/`);

const getUserMatchingUsers = (credsID) => api.get(`credentials/${credsID}/user-matching/users/`);

const verifyEmailAddress = (email) => api.post(`users/verify/`, { email });

const queryAC = (url, q, page, retrievalId, addFullItem = false, customAcMappings = {}, nodeRunId = null, overrideCredentials = null) => {
	const params = { q, page, retrievalId, addFullItem, ...customAcMappings, nodeRunId, overrideC: overrideCredentials };
	return flowRunApi.get(`${url}`, { params: cleanObjectFromNullishValues(params) });
};

const getTaskLog = (filters) => {
	filters = filters || {};
	return api.get(`workflows/task-log/`, { params: filters });
};
const getPersonalInterfacesTaskLog = (taskLogId) => api.get(`workflows/task-interface-log/${taskLogId}/`);

const getTaskInfo = (taskID) => api.get(`workflows/task-log/${taskID}/`);

// 2022-05-15: getTaskNodes & getNodeRunData are deprecated, they are not in use anymore by the task log.
const getTaskNodes = (taskID) => api.get(`workflows/task-log/${taskID}/nodes/`);
const getNodeRunData = (nodeRunId) => api.get(`runner/node-runs/${nodeRunId}/data/`);

/* ANALYTICS */

const analyticsQueryParams = (filter, dateRangeGTE, dateRangeLTE) => {
	const params = new URLSearchParams({
		filter,
	});
	if (dateRangeGTE) {
		params.append('date_range_gte', dateRangeGTE);
	}
	if (dateRangeLTE) {
		params.append('date_range_lte', dateRangeLTE);
	}
	return params.toString();
};

const analyticsBodyParams = (spaceId, includeAllSpaces, limit = undefined) => {
	const params = {};
	if (spaceId) {
		params.space_id = spaceId;
	}
	if (includeAllSpaces) {
		params.include_all_spaces = includeAllSpaces;
	}
	if (limit) {
		params.limit = limit;
	}
	return params;
};

const getAnalyticsRunsByDay = async (spaceId, includeAllSpaces, dateRangeGTE, dateRangeLTE) => {
	try {
		const queryParams = analyticsQueryParams('days', dateRangeGTE, dateRangeLTE);
		const bodyParams = analyticsBodyParams(spaceId, includeAllSpaces);
		const results = await api.post(`analytics/?${queryParams}`, bodyParams);
		for (const date of Object.keys(results)) {
			const { status_breakdown, total_runs } = results[date];
			results[date] = {
				runs: total_runs,
				errors: status_breakdown.error || 0,
			};
		}
		return results;
	} catch (e) {
		console.error('getAnalyticsRunsByDay  e:, ', e);
		return [];
	}
};

const getAnalyticsRunsByUser = async (spaceId, limit, includeAllSpaces, dateRangeGTE, dateRangeLTE) => {
	try {
		const queryParams = analyticsQueryParams('user', dateRangeGTE, dateRangeLTE);
		const bodyParams = analyticsBodyParams(spaceId, includeAllSpaces, limit);
		const results = await api.post(`analytics/?${queryParams}`, bodyParams);
		const runsByUser = Object.values(results);
		return runsByUser.map((user) => ({
			user_id: user.user,
			runs: user.total_runs,
			errors: user.status_breakdown.error || 0,
			// tasks: null,
		}));
	} catch (e) {
		console.error('getAnalyticsRunsByUser  e:, ', { e });
		return [];
	}
};

const getAnalyticsRunsByFeature = async (spaceId, limit, includeAllSpaces, dateRangeGTE, dateRangeLTE) => {
	try {
		const queryParams = analyticsQueryParams('flow', dateRangeGTE, dateRangeLTE);
		const bodyParams = analyticsBodyParams(spaceId, includeAllSpaces, limit);
		const results = await api.post(`analytics/?${queryParams}`, bodyParams);
		return Object.values(results).map((flow) => ({
			runs: flow.total_runs,
			errors: flow.status_breakdown.error || 0,
			// tasks: null,
			flow_data: flow.flow_data,
		}));
	} catch (e) {
		console.error('getAnalyticsRunsByFeature  e:, ', e);
		return [];
	}
};

const getAnalyticsLatestErrors = async (spaceId, includeAllSpaces, dateRangeGTE, dateRangeLTE) => {
	try {
		const queryParams = analyticsQueryParams('errors', dateRangeGTE, dateRangeLTE);
		const bodyParams = analyticsBodyParams(spaceId, includeAllSpaces);
		const response = await api.post(`analytics/?${queryParams}`, bodyParams);
		return Object.values(response).map((flow) => ({
			user_id: flow.user?.id,
			created_at: flow.created,
			title: flow.workflow?.workflow_title,
		}));
	} catch (e) {
		console.error('getAnalyticsLatestErrors  e:', e);
		return [];
	}
};
/* ANALYTICS */

const saveWidgetTest = (nodeId, widgetValues, widgetValuesLabels) =>
	api.post(`workflow-nodes/${nodeId}/widgets/test/`, { test_data: widgetValues, test_data_labels: widgetValuesLabels });

// Used for when we build url outside, such as authURL.
const getURL = (url) => api.get(url);

const verifyCode = (code) => api.post('users/verify/', { sign_up_code: code });

const setUiElementContextualDataProfileID = (elementID, credentials_configuration) =>
	api.post(`btn-elements/${elementID}/credentials/`, { credentials_configuration });

const setUiElementContextualDataState = (elementID, state) => api.post(`btn-elements/${elementID}/contextual-data/`, { state });

const setNativeElementContextualDataProfileID = (elementID, credentials_configuration) =>
	api.post(`native-elements/${elementID}/credentials/`, { credentials_configuration });

const getWidgetFieldUploadUrl = (widgetId, filename) => flowRunApi.post(`widgets/${widgetId}/generate-signed-url/`, { filename });

const changeWorkflowUserSettings = (workflowId, settings) => api.put(`workflows/${workflowId}/settings/`, settings);

const deleteWorkflow = (workflowID) => api.delete(`workflows/${workflowID}/`);

const shareWorkflow = (workflowID, emails, role, email_message, is_send_mail, is_on, invite_user_role) =>
	api.put(`workflows/${workflowID}/share/`, { role, emails, is_on, is_send_mail, email_message, invite_user_role });

const updateWorkflowRoles = (workflowID, role, groups = [], emails = []) =>
	api.put(`workflows/${workflowID}/roles/`, { role, groups, emails });

const moveWorkflowToOtherProject = (workflowId, projectId) => api.put(`workflows/${workflowId}/projects/${projectId}/`);

const shareWorkflowWithGroups = (workflowID, groups, role, emailMessage, isSendMail, isOn) =>
	api.put(`workflows/${workflowID}/share/groups/`, {
		groups,
		role,
		email_message: emailMessage,
		is_send_mail: isSendMail,
		is_on: isOn,
	});

const getWorkflowCredentialsSuggestions = (workflowID, workflowVersionId) =>
	api.get(`workflows/${workflowID}/versions/${workflowVersionId}/credentials/suggestions/`);

const updateWorkflowCredentialsAccordingToSuggestions = (workflowID, nodesToUpdate, markNotToSuggest, workflowVersionId) =>
	api.put(`workflows/${workflowID}/versions/${workflowVersionId}/credentials/suggestions/`, {
		nodes_to_update: nodesToUpdate,
		mark_not_to_suggest: markNotToSuggest,
	});

const saveWorkflowSettings = (workflowSettingsId, keyVal) => api.patch(`workflow_versions_settings/${workflowSettingsId}/`, { ...keyVal });

const updateWorkflowVersionTrigger = (workflowId, workflowVersionId, config) =>
	api.put(`workflows/${workflowId}/versions/${workflowVersionId}/scheduled_trigger_config/`, { config });

const updateWorkflowSettingImage = (workflowSettingsId, image, key) => {
	const data = new FormData();
	data.append([key], image);
	return api.patch(`workflow_versions_settings/${workflowSettingsId}/`, data, {
		'Content-Type': 'multipart/form-data',
	});
};

const resetDynamicFields = (nodeId, clearAll = false) =>
	fullDataResponseApi.delete(`workflow-nodes/${nodeId}/dynamic-fields/?clearAll=${clearAll}`);

// todo - there is no more progress api
const getWorkflowProgress = (id) => api.get(`workflows/workflow_progress/?id=${id}`);

const continueWidgetExecution = (url, data) => axios.post(url, data);

const addCname = (accountId, serviceId, cname) => api.post(`accounts/${accountId}/cname/`, { service_id: serviceId, cname });

const removeCname = (accountId, serviceId, cname) => api.post(`accounts/${accountId}/cname/delete/`, { service_id: serviceId, cname });

const sendChangeAccountOwnerMail = (accountId, payload) => api.post(`accounts/${accountId}/owner/`, payload);

const acceptChangeAccountOwner = (newOwner) => api.put(`accounts/owner/`, { user: newOwner });

const sendDeleteAccountMail = (accountId) => api.delete(`accounts/${accountId}/`);

const acceptDeleteAccount = (hashedAccountLink) => api.delete(`accounts/${hashedAccountLink}/conformation/`);

const moveWidgetFieldConfiguration = (workflowId, field_id, configuration_type_source, configuration_type_destination) =>
	api.put(`workflows/${workflowId}/configurations/field/`, { field_id, configuration_type_source, configuration_type_destination });

const verifyOTP = (token, email) => api.post(`/one-time-token/users/verification/`, { token, email });

const updateUiElementTestParamsMap = (elementId, paramsMap, paramsMapLabels) => {
	const data = {
		paramsMap,
		paramsMapLabels,
	};
	return api.patch(`btn-elements/${elementId}/params-map/`, data);
};

const updateNativeElementTestParamsMap = (elementId, paramsMap, paramsMapLabels) => {
	const data = {
		paramsMap,
		paramsMapLabels,
	};
	return api.patch(`native-elements/${elementId}/params-map/`, data);
};

const getUserAlerts = () => api.get(`alerts/`, { params: { archived: false } });

const getNativeElement = (elementId) => api.get(`native-elements/${elementId}/`);

const getUiElement = (elementId) => api.get(`btn-elements/${elementId}/`);

const getOptionsForContextualData = (contextId, paramsMap, accountConnectionId, source, searchTerm, paramKey, pageNum, retrievalId) =>
	fullDataResponseApi.post(`contexts/${contextId}/options/`, {
		paramsMap,
		accountConnectionId,
		source,
		term: searchTerm,
		paramKey,
		page: pageNum,
		retrievalId,
	});

const createModule = (workflow, moduleType) => api.post(`modules/${moduleType}/`, workflow);

const getNativeElementsEntities = (platformSlug) => api.get(`native-integration-accounts/${platformSlug}/entities/`);
const getNativeIntegrationDatabaseData = (platformSlug, databaseId, query) =>
	api.get(`native-integration-accounts/${platformSlug}/entities/${databaseId}/`, { params: { q: query } });
const currentUserHasAccessToEntity = (platformSlug, entityId) =>
	api.get(`native-integration-accounts/${platformSlug}/accessability/`, { params: { entityId } });

const updateInjectedElementStyle = (elementId, injectionStyle) =>
	api.patch(`btn-elements/${elementId}/style/injection/`, { injectionStyle });
const updateNativeElement = (elementId, data) => api.patch(`native-elements/${elementId}/`, { data });

const searchServiceActions = (queryString) => api.get(`actions/`, { params: { q: queryString } });

/**
 * Services
 */
const getAvailableApps = () => autoPaginatedApi.get('services/');
const getAppData = (appKey) => api.get(`services/${appKey}/`);
const getAppActions = (appKey, version) => {
	let path;
	if (version) {
		path = `services/${appKey}/${version}/actions/`;
	} else {
		throw new Error(`Missing version in getAppActions for service ${appKey}`);
		//path = `services/${appKey}/actions/`;
	}
	return api.get(path);
};
const getProfileLimits = (serviceID) => api.get(`services/${serviceID}/auth/limits/`);
const getContextsOfService = (serviceId) => api.get('contexts/', { params: { service: serviceId } });

const getPlacementsOfService = (serviceId) => autoPaginatedApi.get('placements/', { params: { service: serviceId } });
const getPlacement = (id) => api.get(`placements/${id}/`);

const getServiceActionCollections = () => api.get(`${SERVICE_ACTION_COLLECTION_API_RESOURCE_NAME}/`);
const getServiceActionCollection = (serviceActionCollectionId) =>
	api.get(`${SERVICE_ACTION_COLLECTION_API_RESOURCE_NAME}/${serviceActionCollectionId}/`);
const getServiceActionCollectionActions = (serviceCollectionId) =>
	api.get(`${SERVICE_ACTION_COLLECTION_API_RESOURCE_NAME}/${serviceCollectionId}/actions`);

// Version-aware endpoints
const getServiceAuthUrl = (serviceId, params, version) => {
	let path;
	if (version) {
		path = `services/${serviceId}/${version}/auth/`;
	} else {
		//throw new Error(`Missing version in getServiceAuthUrl for service ${serviceId}`);
		path = `services/${serviceId}/auth/`;
	}
	return api.get(path, { params });
};
const connectService = (serviceSlug, qs, version) => {
	let path;
	if (version) {
		path = `services/${serviceSlug}/${version}/auth/oauth/return`;
	} else {
		//throw new Error(`Missing version in getAconnectServiceppActions for service ${serviceSlug}`);
		path = `services/${serviceSlug}/auth/oauth/return`;
	}
	return api.get(path, { params: qs });
};
const addCustomProfile = (serviceID, authKeys, version) => {
	let path;
	if (version) {
		path = `services/${serviceID}/${version}/auth/custom/`;
	} else {
		//throw new Error(`Missing version in addCustomProfile for service ${serviceID}`);
		path = `services/${serviceID}/auth/custom/`;
	}
	return api.post(path, authKeys);
};

const resetWidgetFieldTestStatus = (nodeId, widget_field_id) =>
	api.post(`workflow-nodes/${nodeId}/reset_widget_field_test_status/`, { widget_field_id });

/**
 * Alerts
 */
const getAlert = (alertId) => api.get(`alerts/${alertId}/`);
const updateAlert = (alert) => api.patch(`alerts/${alert.id}/`, alert);
const completeAlert = (alertId) => api.post(`alerts/${alertId}/complete/`);

/**
 * Workflow Configuration related apis
 */
const WORKFLOW_CONFIGURATION_API_PATH = 'workflow-configurations';
const createConfigurationForWorkflow = (workflowId, isGeneralConfiguration) =>
	api.post(`workflows/${workflowId}/configuration/`, {
		generalConfig: isGeneralConfiguration,
		userSpecificConfig: !isGeneralConfiguration,
	});
const getConfiguration = (configurationId) => api.get(`${WORKFLOW_CONFIGURATION_API_PATH}/${configurationId}/`);
const addInterfaceToConfiguration = (configurationId) => api.post(`${WORKFLOW_CONFIGURATION_API_PATH}/${configurationId}/interface/`);

const saveConfigurationsResponses = (workflowID, response, applyConfigurationsResponseOnDraft) =>
	api.put(`workflows/${workflowID}/configurations/respond/`, { response, applyConfigurationsResponseOnDraft });

const saveConfigurationFieldResponse = (configurationId, interfaceUUID, fieldUUID, response) =>
	api.put(`${WORKFLOW_CONFIGURATION_API_PATH}/${configurationId}/interfaces/${interfaceUUID}/fields/${fieldUUID}/respond/`, { response });
const createConfigurationFieldForWorkflow = (workflowId, fieldValue, isGeneralConfiguration = true) =>
	api.post(`workflows/${workflowId}/configurations/field/text-input/`, { generalConfig: isGeneralConfiguration, fieldValue });

/**
 * Installables related apis
 */
const INSTALLABLES_API_PATH = 'installables';
const getInstallables = () => api.get(`${INSTALLABLES_API_PATH}/`);
const getInstallable = (installableId) => api.get(`${INSTALLABLES_API_PATH}/${installableId}/`);
const createInstallableForWorkflow = (workflowId) => api.post(`workflows/${workflowId}/installables/`);
const getInstallableVersions = (installableId) => api.get(`${INSTALLABLES_API_PATH}/${installableId}/versions/`);
const getInstallableVersion = (installableId, installableVersionId) =>
	api.get(`${INSTALLABLES_API_PATH}/${installableId}/versions/${installableVersionId}/`);
const getInstalledWorkflows = (installableId) => api.get(`${INSTALLABLES_API_PATH}/${installableId}/workflows`);
const installInstallableVersion = (installableId, installableVersionId, fields) =>
	dispatchAndWaitTask(Tasks.InstallInstallableVersion, {
		/*id: installableId,*/ installable_version_id: installableVersionId,
		answered_fields: fields,
	});

const addFieldToWidgetAPI = (workflowId, payload) => api.post(`workflows/${workflowId}/configurations/field/`, payload);

/**
 * Workflow Versions related apis
 */
const getWorkflowVersion = (workflowId, workflowVersionId) => api.get(`workflows/${workflowId}/versions/${workflowVersionId}/`);
const removeWorkflowVersion = (workflowId, workflowVersionId) => api.delete(`workflows/${workflowId}/versions/${workflowVersionId}/`);
const updateWorkflowVersion = (workflowId, workflowVersionId, partialVersionObject = {}) =>
	api.patch(`workflows/${workflowId}/versions/${workflowVersionId}/`, partialVersionObject);

const restoreWorkflowVersionAsDraft = (workflowId, version, copy_configurations_response_from_version) =>
	dispatchAndWaitTask(Tasks.RestoreWorkflow, {
		/*id: workflowId,*/ workflow_version_id: version,
		copy_configurations_response_from_version,
	});

/**
 * Process reports
 */

const getProcesses = () => apigw.get(`processes`);
const getProcessReport = (processId) => apigw.get(`processes/${processId}`);

const getAppsWorkspaces = () => api.get('workflows/apps/');
/**
 * AI Assistant
 */
const invokeAI = (url, params) => api.post(url, params);

const invokeAISSE = (url, params) =>
	fetch(url, {
		method: 'POST',
		body: JSON.stringify(params),
		headers: {
			'Content-Type': 'application/json',
			Authorization: api.defaults.headers.common.Authorization,
		},
	});

const generateUploadUrl = (filename) => api.post(`generate-file-upload-url/`, { filename });

const getGuidedTutorials = () => api.get(`/tutorials/`);
const saveFavoriteApps = (userId, appsIds) => api.put(`users/${userId}/favorite-apps/`, { apps: appsIds });
const requestFavoriteApp = (userId, id) => api.post(`users/${userId}/requests/apps/${id}/`);
const deleteFavoriteAppRequest = (userId, id) => api.delete(`users/${userId}/requests/apps/${id}/`);

const getDirectoryApps = (page, service_name, specificIds) => {
	const searchParams = new URLSearchParams();
	if (page) {
		searchParams.append('page', page);
	}
	if (service_name) {
		searchParams.append('service_name', service_name);
	}
	if (specificIds?.length > 0) {
		specificIds.forEach((id) => {
			searchParams.append('id', id);
		});
	}
	return fullDataResponseApi.get(`service-directories/?${searchParams.toString()}`);
};

export const getAiSuggestion = (input) => api.post(`assistant/autofill/`, input);

/**
 * INSPIRATION CENTER
 */
const getInspirationTags = () => api.get(`/ideas/tags/`);
const getInspirationIdeas = (page, tags, sortByWorkspaceFirst, type) => {
	const searchParams = new URLSearchParams();
	if (page) {
		searchParams.append('page', page);
	}
	if (type) {
		searchParams.append('type', type);
	}
	if (sortByWorkspaceFirst) {
		searchParams.append('ordering', '-type');
	}
	if (tags?.length > 0) {
		tags.forEach((tag) => {
			searchParams.append('tag', tag);
		});
	}
	return fullDataResponseApi.get(`ideas/?${searchParams.toString()}`);
};
const getIdea = (ideaId) => api.get(`ideas/${ideaId}/`);
const saveIdeaToAccount = (ideaId) => api.post(`ideas/${ideaId}/save/`);
const getIdeaComments = (ideaId, page) => {
	const searchParams = new URLSearchParams();
	if (page) {
		searchParams.append('page', page);
	}
	return fullDataResponseApi.get(`ideas/${ideaId}/comments/?${searchParams.toString()}`);
};
const getIdeaComment = (ideaId, commentId) => api.get(`ideas/${ideaId}/comments/${commentId}/`);
const addIdeaComment = (ideaId, commentContent) => api.post(`ideas/${ideaId}/comments/`, { content: commentContent });
const deleteIdeaComment = (ideaId, commentId) => api.delete(`ideas/${ideaId}/comments/${commentId}/`);
const editIdeaComment = (ideaId, commentId, commentContent) =>
	api.patch(`ideas/${ideaId}/comments/${commentId}/`, { content: commentContent });
const likeIdeaComment = (ideaId, commentId) => api.post(`ideas/${ideaId}/comments/${commentId}/like/`);
const unlikeIdeaComment = (ideaId, commentId) => api.delete(`ideas/${ideaId}/comments/${commentId}/like/`);
const voteForIdea = (ideaId) => api.post(`ideas/${ideaId}/vote/`);
const unvoteForIdea = (ideaId) => api.delete(`ideas/${ideaId}/vote/`);
const archiveIdea = (ideaId) => api.delete(`ideas/${ideaId}/`);
const createIdea = (params = {}) => api.post(`ideas/`, params);

/* GRAPH */
const getWorkflowVersionGraph = (workflowId, workflowVersionId) => api.get(`workflows/${workflowId}/versions/${workflowVersionId}/graph/`);

// steps: action on node, that may affect multiple nodes, that's why we call it step, not node
const createStep = (workflowId, step = {}) => {
	try {
		return api.post(`workflows/${workflowId}/steps/`, { ...step });
	} catch (err) {
		throw new NodeCreationError(ErrorHandlingService.extractErrorMessage(err) || 'Unable to perform action');
	}
};
const deleteStep = (workflowId, nodeId) => api.delete(`workflows/${workflowId}/steps/${nodeId}/`);
const duplicateStep = (workflowId, nodeId) => api.post(`workflows/${workflowId}/steps/${nodeId}/duplicate/`);
const moveStep = (workflowId, nodeId, edge) => api.put(`workflows/${workflowId}/steps/${nodeId}/position/`, { edge });
const setContextualStepState = (workflowId, enabled) => api.patch(`workflows/${workflowId}/steps/contextual/`, { enabled });
// branches-specific endpoints

/* branch-paths */
const addBranchPath = (branchRootId) => api.post(`workflows/branches/${branchRootId}/paths/`);
const deleteBranchPath = (branchRootId, branchPathId) => api.delete(`workflows/branches/${branchRootId}/paths/${branchPathId}/`);
const updateBranchPath = (branchRootId, branchPathId, partialObjectToUpdate) =>
	api.patch(`workflows/branches/${branchRootId}/paths/${branchPathId}/`, partialObjectToUpdate);
/* branch-variables */
const addBranchVariable = (branchRootId) => api.post(`workflows/branches/${branchRootId}/variables/`);
const deleteBranchVariable = (branchRootId, branchVariableId) =>
	api.delete(`workflows/branches/${branchRootId}/variables/${branchVariableId}/`);
const updateBranchVariable = (branchRootId, branchVariableId, partialObjectToUpdate) =>
	api.patch(`workflows/branches/${branchRootId}/variables/${branchVariableId}/`, partialObjectToUpdate);
const updateBranchVariableMapping = (branchRootId, branchPathId, branchId, partialObjectToUpdate) =>
	api.patch(`workflows/branches/${branchRootId}/variables/${branchPathId}/mappings/${branchId}/`, partialObjectToUpdate);
/* GRAPH */

// ALERTS
const getAlertsByInboxId = (page = 1, id, cursor = null, archived = false) => {
	const searchParams = new URLSearchParams();
	searchParams.append('archived', archived);
	searchParams.append('page', page);
	if (cursor) {
		searchParams.append('cursor', cursor);
	}
	if (id && id !== STATIC_INBOXES_IDS.ALL_INBOX_ID) {
		// if id is not provided, it will return all ALERTS
		searchParams.append('inbox', id);
	}
	return fullDataResponseApi.get(`alerts/?${searchParams.toString()}`);
};
const getAlertsStats = () => api.get(`alerts/stats/`);

const selectWorkspaceForSlack = async (userId, token, accountId) =>
	api.post(`users/${userId}/slack/slash-commands/connections/`, { account_id: accountId, token });

/* Subflows */
const getSubflows = (projectId) => autoPaginatedApi.get(`workflows/`, { params: { project: projectId, flow_type: FlowType.Sub } });
const getSubflowParam = (id) => api.get(`sub-workflows-configurations/${id}/params/`);
const updateSubflowParams = (id, params) => api.put(`sub-workflows-configurations/${id}/`, { params });
const sendTestDataToSubflowInputs = (subFlowId, data) => api.post(`/workflows/${subFlowId}/sub-flow/sample/`, { input_data: data });

/* BILLING */
const purchaseSingleProduct = (product_type, plan_id, unit_quantity) =>
	api.put(`billing/subscriptions/products/${product_type}/`, { plan_id, unit_quantity });
const getAccountSubscriptions = () => api.get(`billing/subscriptions/`);
const upgradeBillingPlans = (change_plans_details, customer_invoice_name) =>
	api.put(`billing/subscriptions/products/`, { change_plans_details, customer_invoice_name });
const setPaymentMethod = (payment_method_id, name) => api.post(`billing/payments/cards/`, { payment_method_id, name });
const getPaymentCards = () => api.get(`billing/payments/cards/`);
const getPaymentMethods = () => api.get(`billing/payments/methods/`);
const getPaymentIntent = () => api.post(`billing/payments/intents/`);
const getBillingProducts = () => api.get(`billing/products/`);
const getBillingPaywall = () => api.get(`billing/products/paywall/`);
const setIsSmartUpgradeEnabled = (is_smart_upgrade_enabled) => api.put(`billing/subscriptions/`, { is_smart_upgrade_enabled });
const cancelProductDowngrade = (product_type) => api.delete(`billing/subscriptions/updates/${product_type}`);
const getCustomerData = () => api.get(`billing/subscriptions/customer/`);
const getBillingPreview = (pricePreviewInput) => {
	const searchParams = new URLSearchParams();
	Object.keys(pricePreviewInput).forEach((key) => {
		searchParams.append(key, pricePreviewInput[key]);
	});
	return api.get(`billing/subscriptions/preview/?${searchParams.toString()}`);
};
// billing usage
const getUsageAnalytics = (feature_id) => {
	const searchParams = new URLSearchParams();
	searchParams.append('feature_id', feature_id);
	return api.get(`billing/analytics/usage/?${searchParams.toString()}`);
};
const getFeatureUsageHistory = (feature_id) => {
	const searchParams = new URLSearchParams();
	searchParams.append('feature_id', feature_id);
	return api.get(`billing/analytics/usage/history/?${searchParams.toString()}`);
};
const getSubscriptionHistory = (product_id) => {
	const searchParams = new URLSearchParams();
	searchParams.append('product_id', product_id);
	return fullDataResponseApi.get(`billing/analytics/subscriptions/history/?${searchParams.toString()}`);
};
const getEntitlements = () => api.get(`billing/entitlements/`);
const getInvoices = () => fullDataResponseApi.get(`billing/subscriptions/invoices/`);
const deletePaymentMethod = (payment_id) => api.delete(`billing/payments/methods/${payment_id}/`);
const setDefaultPaymentMethod = (payment_id) => api.put(`billing/payments/methods/${payment_id}/`);
const getOrganizationSettings = () => api.get(`billing/customer/information/`);
const saveOrganizationSettings = ({ city, addressLine1, addressLine2, countryOrRegion, taxId, organizationName, postalCode }) =>
	api.put(`billing/customer/information/`, {
		name: organizationName,
		tax_id: taxId,
		address: {
			postal_code: postalCode,
			country: countryOrRegion,
			line1: addressLine1,
			line2: addressLine2,
			city,
		},
	});
const saveBillingEmailsConfig = (emailsConfig) => api.put(`billing/emails/`, emailsConfig);
const getBillingEntitlementPlans = () => api.get(`billing/entitlements/plans/`);
const requestFeatureAccess = (flow_hash) => api.post(`billing/permissions/request-access/`, { flow_hash });
const getProductAutomatedSubscriptionChanges = (product_id) => {
	const searchParams = new URLSearchParams();
	searchParams.append('product_id', product_id);
	return fullDataResponseApi.get(`billing/analytics/subscriptions/history/automated/?${searchParams.toString()}`);
};
export const processes = buildProcessesApi(api);

export const notifications = buildNotificationsApi(api);

export const inboxes = buildInboxesApi(api);

/* AI Assistant Thread operations */
export const createAssistantThread = (nodeId) =>
	api.post(new URL(`${process.env.VUE_APP_API}${`workflow-nodes/${nodeId}`}/assistant/create_thread/`));

/* AI Assistant API Docs */
const AI_ASSISTANT_API_DOCS_ENDPOINT = 'ai-assistant-api-docs/';

const {
	create: addAiAssistantApiDocs,
	partialUpdate: updateAiAssistantApiDoc,
	deleteMethod: deleteAiAssistantApiDoc,
} = useAiAssistantApiDocsCrud();
const listAiAssistantApiDocs = (projectId) => autoPaginatedApi.get(`${AI_ASSISTANT_API_DOCS_ENDPOINT}?project_id=${projectId}`);

/* Account email domain api methods */
const {
	get: getAccountEmailDomain,
	list: listAccountEmailDomains,
	create: createAccountEmailDomain,
	partialUpdate: updateAccountEmailDomain,
	deleteMethod: deleteAccountEmailDomain,
	verifyDkim,
	sendVerifyReplyToEmail,
	verifyReplyToEmailOtp,
} = useAccountEmailDomainCrud();

const getIssues = (page, filterBy = {}) => {
	const params = Object.entries(filterBy).reduce((params, [key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((v) => params.append(key, v));
		} else {
			params.append(key, value);
		}
		return params;
	}, new URLSearchParams());
	if (page) {
		params.append('page', page);
	}
	return fullDataResponseApi.get('issues/', { params });
};

export const requestPortalAuthCode = ({ email, workflow_hash }) => api.post('portal-auth/code-request/', { email, workflow_hash });
export const exchangePortalAuthCode = ({ email, workflow_hash, code }) =>
	api.post('portal-auth/code-exchange/', { email, workflow_hash, code });

export const getWorkflowRunProperties = (workflowHash) => api.get(`workflows/${workflowHash}/properties/`);
const listCustomDomains = () => autoPaginatedApi.get('custom-domains/');
const addCustomDomain = (domain) => api.post('custom-domains/', { domain });
const verifyCustomDomain = (domainId) => api.get(`custom-domains/${domainId}/verify/`);
const deleteCustomDomain = (domainId) => api.delete(`custom-domains/${domainId}/`);
const setDefaultCustomDomain = (domainId, isDefault) => api.patch(`custom-domains/${domainId}/default/`, { is_default: isDefault });

export default {
	getAppsWorkspaces,
	archiveIdea,
	saveFavoriteApps,
	requestFavoriteApp,
	deleteFavoriteAppRequest,
	getDirectoryApps,
	joinWorkspace,
	setToken,
	removeToken,
	loginWithUserPass,
	loginWithToken,
	loginWithGoogleToken,
	loginWithGoogleCode,
	loginWithSSOCode,
	loginWithSSO,
	logout,
	changePassword,
	forgotPassword,
	resetPassword,
	validateReset,
	getOneTimeToken,
	verifyOneTimeToken,
	getUser,
	getGroups,
	getGroup,
	setGroupUsers,
	removeGroupUser,
	addGroupUser,
	createGroup,
	updateGroup,
	deleteGroup,
	syncGroup,
	registerUser,
	createFlow,
	getNodesByIds,
	createFlowFromExistingNodes,

	/*GRAPH*/
	getWorkflowVersionGraph,
	createStep,
	duplicateStep,
	deleteStep,
	moveStep,
	setContextualStepState,
	addBranchPath,
	deleteBranchPath,
	updateBranchPath,
	addBranchVariable,
	deleteBranchVariable,
	updateBranchVariable,
	updateBranchVariableMapping,
	/*GRAPH*/

	selectWorkspaceForSlack,
	/** services */
	getAvailableApps,
	getServiceActionCollections,
	getServiceActionCollection,
	getServiceActionCollectionActions,
	getAppData,
	getAppActions,
	getUserCredentials,
	getCredential,
	getProjectCredentials,
	connectService,
	getServiceAuthUrl,

	testStep,
	skipTestStep,

	updateNode,
	getFlows,
	getFlowsByCredentials,
	getProjects,
	getAllProjects,
	getProject,
	updateProject,
	getProjectSharedData,
	getAllProjectShareData,
	getProjectPermissions,
	getAllProjectPermissions,
	updateProjectRoles,
	getWorkflow,
	getNode,
	getWidget,
	getWidgets,
	getOptionsForAction,
	getOptionsForWidget,
	getExtraFields,
	toggleNodeType,
	saveWidget,
	assignWidgetFieldCredentialsSourceField,
	setWidgetFieldCredentialsSourceField,
	addCustomProfile,
	getProfileLimits,
	createProject,
	collaborateNewProject,
	collaborateGroupsNewProject,
	updateFlow,
	publishFlow,
	testFlow,
	updateFlowIsActive,
	duplicateWorkflow,
	toggleWorkflowIsOnSettings,
	changeGroupFeatureVisibility,
	applyExistingWorkflowVisibilityToAllGroupMembers,
	getWorkflowSharedData,
	getWorkflowLockedCredentials,
	saveUserProfile,
	saveUserProfileAvatar,
	updateAccount,
	saveUserMetadata,
	getAccount,
	getAccountMembers,
	removeUserFromAccount,
	inviteToAccount,
	revokeInvitation,
	updateRoles,

	updateUsersState,

	resendInvitation,
	resendConfirmationEmail,
	updateCredentialsName,
	getCredentialsWorkflows,

	disconnectCredentials,
	updateCredentialAccess,
	testConnection,
	getUserMatchingUsers,
	verifyEmailAddress,
	queryAC,
	saveWidgetTest,
	getURL,
	verifyCode,
	getWidgetFieldUploadUrl,
	changeWorkflowUserSettings,
	deleteWorkflow,
	shareWorkflow,
	updateWorkflowRoles,
	moveWorkflowToOtherProject,
	shareWorkflowWithGroups,
	getWorkflowCredentialsSuggestions,
	updateWorkflowCredentialsAccordingToSuggestions,
	resetDynamicFields,
	getWorkflowProgress,
	continueWidgetExecution,
	/*
	 * WORKSPACES/ACCOUNTS
	 */
	saveWorkspaceAvatar,
	addCname,
	removeCname,
	sendChangeAccountOwnerMail,
	acceptChangeAccountOwner,
	sendDeleteAccountMail,
	acceptDeleteAccount,
	updatePublicFlowIcon,
	updateUiElementTestParamsMap,
	setUiElementContextualDataProfileID,
	setUiElementContextualDataState,
	updateNativeElementTestParamsMap,
	setNativeElementContextualDataProfileID,
	getNativeElement,
	updateInjectedElementStyle,
	updateNativeElement,
	getUiElement,
	getContextsOfService,
	getPlacementsOfService,
	getPlacement,
	getOptionsForContextualData,
	createModule,
	getUserAlerts,

	getNativeElementsEntities,
	getNativeIntegrationDatabaseData,
	currentUserHasAccessToEntity,
	verifyOTP,
	resetWidgetFieldTestStatus,
	searchServiceActions,
	getAlert,
	updateAlert,
	completeAlert,
	generateUploadUrl,
	getGuidedTutorials,
	/**
	 * Workflow Configuration
	 */
	createConfigurationForWorkflow,
	getConfiguration,
	addInterfaceToConfiguration,
	saveConfigurationsResponses,
	saveConfigurationFieldResponse,
	createConfigurationFieldForWorkflow,

	/**
	 * Installables
	 */
	getInstallables,
	getInstallable,
	createInstallableForWorkflow,
	getInstallableVersions,
	getInstallableVersion,
	installInstallableVersion,
	getInstalledWorkflows,
	addFieldToWidgetAPI,

	/**
	 * Workflow Versions
	 */
	getWorkflowVersion,
	removeWorkflowVersion,
	updateWorkflowVersion,
	restoreWorkflowVersionAsDraft,
	/**
	 * Workflow Versions
	 */
	sendReminderToUseDesktop,
	setFlowWaitingForFlag,
	getFlowWaitingForFlag,
	// Task log
	getTaskLog,
	getPersonalInterfacesTaskLog,
	getNodeRunData,
	getTaskNodes,
	getTaskInfo,
	updateWorkflowVersionTrigger,
	// ANALYTICS
	getAnalyticsRunsByDay,
	getAnalyticsRunsByUser,
	getAnalyticsRunsByFeature,
	getAnalyticsLatestErrors,
	// ANALYTICS
	// Workflow settings
	saveWorkflowSettings,
	updateWorkflowSettingImage,

	moveWidgetFieldConfiguration,

	signForExtensionUpdate,

	/**
	 * BILLING
	 */
	getBillingProducts,
	getBillingPaywall,
	setIsSmartUpgradeEnabled,
	cancelProductDowngrade,
	getCustomerData,
	upgradeBillingPlans,
	setPaymentMethod,
	getPaymentMethods,
	getPaymentCards,
	getPaymentIntent,
	getAccountSubscriptions,
	purchaseSingleProduct,
	getBillingPreview,
	getUsageAnalytics,
	getFeatureUsageHistory,
	getSubscriptionHistory,
	getEntitlements,
	deletePaymentMethod,
	setDefaultPaymentMethod,
	getInvoices,
	getOrganizationSettings,
	saveOrganizationSettings,
	saveBillingEmailsConfig,
	getBillingEntitlementPlans,
	requestFeatureAccess,
	getProductAutomatedSubscriptionChanges,

	/**
	 * Processes
	 */
	processes,

	/**
	 * Process reports
	 */
	getProcesses,
	getProcessReport,

	/**
	 * AI
	 */
	invokeAI,
	invokeAISSE,
	sendContinueInstallationSignupMail,

	makeSyncedGroupManual,

	triggerSetup,
	getMarketplace,

	inboxes,
	notifications,
	/**
	 * INSPIRATION CENTER
	 */
	getInspirationTags,
	getInspirationIdeas,
	saveIdeaToAccount,
	getIdeaComments,
	getIdeaComment,
	getIdea,
	addIdeaComment,
	deleteIdeaComment,
	editIdeaComment,
	likeIdeaComment,
	unlikeIdeaComment,
	voteForIdea,
	unvoteForIdea,
	createIdea,

	/**
	 * BUTTON DROPDOWNS
	 */
	deleteButtonDropdown,
	getButtonDropdown,
	getButtonDropdowns,
	updateButtonDropdown,
	createButtonDropdown,
	/**
	 * BUTTON DROPDOWNS
	 */

	/**
	 * ALERTS
	 * */
	getAlertsByInboxId,
	getAlertsStats,

	getSubflows,
	getSubflowParam,

	updateSubflowParams,
	sendTestDataToSubflowInputs,

	/* AI Assistant Thread operations */
	createAssistantThread,

	/**
	 * AI Assistant API Docs
	 * */
	listAiAssistantApiDocs,
	addAiAssistantApiDocs,
	deleteAiAssistantApiDoc,
	updateAiAssistantApiDoc,

	/*
	 * Account email domain api methods
	 */

	getAccountEmailDomain,
	listAccountEmailDomains,
	createAccountEmailDomain,
	updateAccountEmailDomain,
	deleteAccountEmailDomain,
	verifyDkim,
	sendVerifyReplyToEmail,
	verifyReplyToEmailOtp,

	/*
	 * Workspace domain api methods
	 */
	listCustomDomains,
	addCustomDomain,
	deleteCustomDomain,
	verifyCustomDomain,
	setDefaultCustomDomain,

	/**
	 * issues
	 */
	getIssues,

	runnerApi,
};
